import React, { useState, useRef, useEffect } from 'react';
import { Popconfirm, Modal } from 'antd';
import { Button, Typography, Collapse, Progress, Upload, Image } from 'components';
import { PlusOutlined, FullscreenOutlined, DeleteOutlined, CheckCircleFilled } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'components';
import { ArrowBottomIcon } from 'components/common/icons/ArrowBottomIcon';
import { useOrderDetails } from './useOrderDetails';
import { StatusBadge } from 'components/warehouse';
import styles from './styles.module.scss';

const { Title, Text } = Typography;

type FileType = Parameters<NonNullable<UploadProps['beforeUpload']>>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const OrderDetails: React.FC = () => {
  const {
    order,
    isLoading,
    handleScanItem,
    handleFileUpload,
    handleDeleteAttachment,
    handleEditItem,
    handleRaiseClaim,
    expandedItemId,
    showAllDoneModal,
    handleCompleteOrder,
    handleBackToItems,
    allItemsChecked,
    handleShowCompleteModal,
  } = useOrderDetails();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileLists, setFileLists] = useState<Record<string, UploadFile[]>>({});
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const uploadingRef = useRef<Record<string, boolean>>({});

  // Transform attachments from API to UploadFile format
  useEffect(() => {
    if (order && order.itemsResponseList) {
      const initialFileLists: Record<string, UploadFile[]> = {};

      order.itemsResponseList.forEach((item) => {
        if (item.attachments && item.attachments.length > 0) {
          initialFileLists[item.id] = item.attachments.map((attachment: any) => ({
            uid: attachment.id,
            name: attachment.fileName,
            status: 'done' as const,
            url: attachment.files?.ATTACHMENTS,
            size: attachment.size,
            type: `image/${attachment.extension}`,
            attachmentType: attachment.attachmentType, // Preserve attachment type
          }));
        }
      });

      setFileLists(initialFileLists);
    }
  }, [order]);

  // Auto-expand item when coming from outbound "Go To Source"
  useEffect(() => {
    if (expandedItemId) {
      setActiveKeys([expandedItemId]);
    }
  }, [expandedItemId]);

  if (isLoading || !order) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Text>Loading order details...</Text>
        </div>
      </div>
    );
  }

  const totalItems = order.itemsResponseList?.length || 0;
  const completedItems = Math.round(((order.completedItemsPercentage || 0) / 100) * totalItems);

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange =
    (itemId: string): UploadProps['onChange'] =>
    async ({ fileList: newFileList }) => {
      const previousList = fileLists[itemId] || [];

      // Find new files that were just added
      const newFiles = newFileList.filter((newFile) => !previousList.find((prevFile) => prevFile.uid === newFile.uid));

      // Update file list immediately
      setFileLists((prev) => ({
        ...prev,
        [itemId]: newFileList,
      }));

      // Prevent double upload - check if already uploading
      if (newFiles.length > 0 && !uploadingRef.current[itemId]) {
        const filesToUpload = newFiles.filter((file) => file.originFileObj);

        if (filesToUpload.length > 0) {
          uploadingRef.current[itemId] = true;
          // Upload all new files at once
          const result = await handleFileUpload(itemId, filesToUpload);

          // Update file UIDs with attachment IDs from backend
          if (result.success && result.attachmentIds.length > 0) {
            setFileLists((prev) => {
              const currentList = prev[itemId] || [];
              const updatedList = currentList.map((file) => {
                // Find the corresponding uploaded file
                const uploadedFileIndex = filesToUpload.findIndex((f) => f.uid === file.uid);
                if (uploadedFileIndex !== -1 && result.attachmentIds[uploadedFileIndex]) {
                  return {
                    ...file,
                    uid: result.attachmentIds[uploadedFileIndex],
                    status: 'done' as const,
                  };
                }
                return file;
              });
              return {
                ...prev,
                [itemId]: updatedList,
              };
            });
          }

          uploadingRef.current[itemId] = false;
        }
      }
    };

  const handleRemove = () => async () => {
    // Return false to prevent automatic removal - Popconfirm will handle it
    return false;
  };

  const handleCustomRequest = (): UploadProps['customRequest'] => async (options) => {
    const { onSuccess } = options;
    // Just mark as success - actual upload happens in handleChange
    onSuccess?.('ok');
  };

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.orderInfo}>
          <div className={styles.orderTitleRow}>
            <Title level={4} className={styles.orderTitle}>
              Order: {order.uuid}
            </Title>
            <StatusBadge status={order.status} type="inbound" />
          </div>
          <Text className={styles.supplierName}>Supplier: {order.supplier?.name}</Text>
          {order.name && <Text className={styles.orderName}>{order.name}</Text>}
        </div>

        <div className={styles.progressSection}>
          <Progress
            percent={order.completedItemsPercentage || 0}
            strokeColor="#05796B"
            showInfo={false}
            className={styles.progressBar}
          />
          <div className={styles.progressHeader}>
            <Text className={styles.progressLabel}>Completed Items</Text>
            <Text className={styles.progressValue}>
              {completedItems}/{totalItems}
            </Text>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.itemsList}>
          {order.itemsResponseList?.map((item) => (
            <div key={item.id} className={styles.itemCard}>
              <Collapse
                ghost
                expandIconPosition="end"
                className={styles.collapse}
                activeKey={activeKeys}
                onChange={(keys) => setActiveKeys(keys as string[])}
                expandIcon={({ isActive }) => (
                  <div
                    className={styles.expandIconWrapper}
                    style={{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  >
                    <ArrowBottomIcon />
                  </div>
                )}
                items={[
                  {
                    key: item.id,
                    label: (
                      <div className={styles.itemHeader}>
                        <Text className={styles.itemSku}>{item.sku}</Text>
                        <StatusBadge status={item.status} type="item" />
                      </div>
                    ),
                    children: (
                      <div className={styles.itemDetails}>
                        <div className={styles.quantityRow}>
                          <Text className={styles.quantityText}>
                            <span className={styles.quantityLabel}>Quantity:</span>{' '}
                            <span className={styles.quantityValue}>{item.quantity} item</span>
                          </Text>
                        </div>
                        {item.description && (
                          <div className={styles.descriptionRow}>
                            <Text className={styles.descriptionText}>{item.description}</Text>
                          </div>
                        )}
                        <div className={styles.uploadSection}>
                          <Upload
                            listType="picture-card"
                            fileList={fileLists[item.id] || []}
                            onPreview={handlePreview}
                            onChange={handleChange(item.id)}
                            onRemove={handleRemove()}
                            customRequest={handleCustomRequest()}
                            accept="image/*"
                            multiple
                            className={styles.uploadComponent}
                            showUploadList={{
                              showPreviewIcon: true,
                              showRemoveIcon: (file: any) => {
                                return file.attachmentType !== 'WAREHOUSE_IDENTIFIER';
                              },
                              previewIcon: <FullscreenOutlined />,
                              removeIcon: (file: any) => {
                                return (
                                  <Popconfirm
                                    title="Delete attachment"
                                    description="Are you sure you want to delete this attachment?"
                                    onConfirm={async () => {
                                      if (file.uid && file.name) {
                                        const success = await handleDeleteAttachment(file.uid, file.name);
                                        if (success) {
                                          // Remove file from list after successful deletion
                                          setFileLists((prev) => ({
                                            ...prev,
                                            [item.id]: (prev[item.id] || []).filter((f) => f.uid !== file.uid),
                                          }));
                                        }
                                      }
                                    }}
                                    okText="Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                  >
                                    <DeleteOutlined />
                                  </Popconfirm>
                                );
                              },
                            }}
                          >
                            {(fileLists[item.id]?.length || 0) >= 8 ? null : uploadButton}
                          </Upload>
                        </div>

                        {/* Action Buttons */}
                        <div className={styles.itemActions}>
                          <Button type="link" className={styles.editButton} onClick={() => handleEditItem(item)}>
                            Edit Item
                          </Button>
                          <Button
                            type="link"
                            danger
                            className={styles.raiseClaimButton}
                            onClick={() => handleRaiseClaim(item)}
                          >
                            Raise Claim
                          </Button>
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.bottomAction}>
        {allItemsChecked ? (
          <Button
            type="primary"
            size="large"
            onClick={handleShowCompleteModal}
            className={styles.completeActionButton}
            block
          >
            Complete
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={order.status === 'COMPLETED'}
            size="large"
            onClick={handleScanItem}
            className={styles.scanButton}
            block
          >
            Scan Item
          </Button>
        )}
      </div>

      {previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}

      {/* All Done Modal */}
      <Modal
        open={showAllDoneModal}
        footer={null}
        closable={false}
        centered
        width={400}
        className={styles.allDoneModal}
      >
        <div className={styles.allDoneContent}>
          <CheckCircleFilled className={styles.checkIcon} />
          <Title level={2} className={styles.allDoneTitle}>
            All Done
          </Title>
          <Text className={styles.allDoneText}>All items have been successfully matched to the order.</Text>
          <div className={styles.allDoneButtons}>
            <Button onClick={handleBackToItems} className={styles.backButton}>
              Back To Items
            </Button>
            <Button type="primary" onClick={handleCompleteOrder} className={styles.completeButton}>
              Complete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
